<template>
  <div class="customer-details-page">
    <section class="page-header fade-in">
      <div class="header-left">
        <button class="btn btn-light" @click="goBack">
          <i class="bi bi-arrow-left"></i>
          <span>Retour</span>
        </button>
        <div>
          <h1 class="page-title">Détails du client</h1>
          <p class="page-subtitle" v-if="customer">
            {{ customer.fullName || customer.name }}
          </p>
        </div>
      </div>
      <div class="header-actions" v-if="customer">
        <button class="btn btn-secondary" @click="editCustomer">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="deleteCustomer">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </section>

    <section v-if="!loading && customer" class="fade-in">
      <div class="info-grid">
        <div class="info-card">
          <h2 class="section-title">Informations générales</h2>
          <div class="info-line">
            <span class="label">Nom complet</span>
            <span class="value">{{
              customer.fullName || customer.name || "—"
            }}</span>
          </div>
          <div class="info-line">
            <span class="label">Téléphone</span>
            <span class="value">{{ customer.phone || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Société</span>
            <span class="value">{{ customer.society || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Plafond impayé</span>
            <span class="value">{{
              customer.maxUnpaid || customer.max_unpaid
                ? formatAmount(customer.maxUnpaid || customer.max_unpaid)
                : "—"
            }}</span>
          </div>
        </div>

        <div class="info-card">
          <h2 class="section-title">Adresse</h2>
          <div class="info-line">
            <span class="label">Adresse</span>
            <span class="value">{{ customer.address || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Ville</span>
            <span class="value">{{ customer.city || "—" }}</span>
          </div>
        </div>

        <div class="info-card" v-if="customer.remark">
          <h2 class="section-title">Remarque</h2>
          <div class="info-line">
            <span class="value">{{ customer.remark || "—" }}</span>
          </div>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement du client...</p>
    </div>

    <div v-else class="state-empty">
      <i class="bi bi-emoji-neutral"></i>
      <p>Client introuvable ou supprimé.</p>
      <button class="btn btn-primary" @click="goToList">
        <i class="bi bi-people"></i>
        <span>Voir les clients</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CustomerShow",
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapGetters("customer", {
      customer: "getCustomer",
    }),
    reference() {
      return this.$route.params.reference;
    },
  },
  methods: {
    formatAmount(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    goBack() {
      this.$router.back();
    },
    goToList() {
      this.$router.push({ name: "customers-list" });
    },
    editCustomer() {
      this.$router.push({
        name: "customers-edit",
        params: { reference: this.reference },
      });
    },
    async deleteCustomer() {
      const confirmed = window.confirm(
        "Êtes-vous sûr de vouloir supprimer ce client ?"
      );
      if (!confirmed) return;
      try {
        await this.$store.dispatch("customer/destroy", this.reference);
        this.$store.dispatch("notification/store", {
          status: "success",
          message: "Client supprimé avec succès.",
        });
        this.goToList();
      } catch (error) {
        console.error("Error deleting customer", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de supprimer le client.",
        });
      }
    },
    async fetchCustomer() {
      this.loading = true;
      try {
        await this.$store.dispatch("customer/show", this.reference);
      } catch (error) {
        console.error("Error fetching customer", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Client introuvable.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchCustomer();
  },
  beforeDestroy() {
    this.$store.dispatch("customer/reset");
  },
};
</script>

<style scoped>
.customer-details-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.page-title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary);
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.info-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
}

.label {
  color: var(--text-light);
  font-weight: 600;
}

.value {
  color: var(--text-primary);
  font-weight: 500;
}

.value.badge {
  background: rgba(63, 81, 181, 0.1);
  color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
}

.loading-state,
.state-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: var(--text-secondary);
}

.state-empty i {
  font-size: 3rem;
  color: var(--primary-color);
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .customer-details-page {
    padding: 1rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
